import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Checkbox, DatePicker, Divider, Dropdown, Image, List, Modal, Space, Spin, Table, Tag, Typography, message } from 'antd';
import { FileImageOutlined, ImportOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { ENDPOINTS } from '../helpers/api/apiConfig';
import { formatMeasurementTimestamp, formatUtcTimestamp, measurementDateKey, measurementTimezone } from '../utils/measurementTime';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

interface PatternItem {
    id: number;
    name: string;
    patternType?: string;
}

interface PatternPointItem {
    id: number;
    label?: string | null;
    pointIndex?: number;
    gpsLat?: number | null;
    gpsLon?: number | null;
}

interface MeasurementValue {
    valueType: string;
    numValue?: number | null;
    textValue?: string | null;
    processingStage?: string | null;
}

interface MeasurementFile {
    id: number;
    fileName: string;
    fileType?: string;
    fileSize?: number;
    mimeType?: string;
    signedUrl?: string;
    processingStage?: string | null;
}

interface MeasurementText {
    id: number;
    text: string;
    textType?: string;
    createdAt?: string;
    processingStage?: string | null;
}

interface MeasurementItem {
    id: number;
    measurementCode: string;
    createdAt: string;
    measuredAt?: string | null;
    measuredTimezone?: string | null;
    measuredUtcOffsetMinutes?: number | null;
    timeSource?: string | null;
    timezoneSource?: string | null;
    localPose?: Record<string, any> | null;
    areaId?: number | null;
    area?: { id: number; name: string } | null;
    device?: { id: number; label?: string | null } | null;
    patternId?: number | null;
    pattern?: PatternItem | null;
    patternPointId?: number | null;
    patternPoint?: PatternPointItem | null;
    files?: MeasurementFile[];
    values?: MeasurementValue[];
    texts?: MeasurementText[];
}

const formatValue = (value: any) => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value.toFixed(2) : '—';
    }
    if (typeof value === 'string') return value;
    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
};

const escapeCsvValue = (value: any) => {
    if (value === null || value === undefined) return '';
    const raw = String(value);
    if (raw.includes('"') || raw.includes(',') || raw.includes('\n')) {
        return `"${raw.replace(/"/g, '""')}"`;
    }
    return raw;
};

const downloadBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

const normalizeToString = (value: any) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
};

const formatLocalPose = (pose?: Record<string, any> | null) => {
    const x = Number(pose?.x);
    const y = Number(pose?.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return '—';
    const frame = typeof pose?.frameId === 'string' && pose.frameId ? pose.frameId : 'map';
    return `${frame}: ${x.toFixed(2)}, ${y.toFixed(2)}`;
};

const buildFilters = (rows: any[], key: string, limit = 50) => {
    const values = new Set<string>();
    rows.forEach((row) => {
        const raw = row?.[key];
        const normalized = normalizeToString(raw);
        if (normalized) values.add(normalized);
    });
    return Array.from(values)
        .slice(0, limit)
        .map((value) => ({ text: value, value }));
};

type SorterType = 'number' | 'date' | 'text';

const buildSorter = (key: string, type: SorterType) => {
    if (type === 'number') {
        return (a: any, b: any) => {
            const av = Number(a?.[key]);
            const bv = Number(b?.[key]);
            const aNum = Number.isFinite(av) ? av : -Infinity;
            const bNum = Number.isFinite(bv) ? bv : -Infinity;
            return aNum - bNum;
        };
    }
    if (type === 'date') {
        return (a: any, b: any) => {
            const aTime = a?.[key] ? new Date(a[key]).getTime() : 0;
            const bTime = b?.[key] ? new Date(b[key]).getTime() : 0;
            return aTime - bTime;
        };
    }
    return (a: any, b: any) =>
        normalizeToString(a?.[key]).localeCompare(normalizeToString(b?.[key]));
};

const getInitialDateRange = (): [Dayjs, Dayjs] => {
    try {
        const stored = localStorage.getItem('measurementListDateRange');
        if (stored) {
            const { from, to } = JSON.parse(stored);
            return [dayjs(from), dayjs(to)];
        }
    } catch (error) {
        console.warn('Failed to load measurement list date range', error);
    }
    return [dayjs().subtract(30, 'day'), dayjs()];
};

const MeasurementList: React.FC = () => {
    const navigate = useNavigate();
    const [measurements, setMeasurements] = useState<MeasurementItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [selectedMeasurement, setSelectedMeasurement] = useState<MeasurementItem | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [filesLoading, setFilesLoading] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState<MeasurementFile[]>([]);
    const [areaIndex, setAreaIndex] = useState<Map<number, { id: number; name: string; parentAreaId?: number | null }>>(new Map());
    const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>(getInitialDateRange());
    const latestUploadsHidden = dateRange[1].isBefore(dayjs(), 'day');
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [downloadingZip, setDownloadingZip] = useState(false);

    const filterProcessed = useCallback(
        <T extends { processingStage?: string | null }>(items?: T[]) =>
            (items || []).filter((item) => item.processingStage === 'processed'),
        []
    );

    const goToReports = useCallback((dashboardNav: Record<string, any>, measurementDate?: string | null) => {
        const pointDate = measurementDate ? dayjs(measurementDate) : null;
        const dateNavigation = pointDate?.isValid()
            ? {
                  dateFrom: pointDate.startOf('day').toISOString(),
                  dateTo: pointDate.endOf('day').toISOString(),
              }
            : {};

        navigate('/reports', {
            state: {
                dashboardNav: {
                    ...dashboardNav,
                    ...dateNavigation,
                },
            },
        });
    }, [navigate]);

    const fetchMeasurements = useCallback(async () => {
        setLoading(true);
        try {
            const batchSize = 500;
            let offset = 0;
            let total = 0;
            const all: MeasurementItem[] = [];

            while (true) {
                const res = await axios.get(ENDPOINTS.MEASUREMENTS_V2, {
                    params: {
                        limit: batchSize,
                        offset,
                        from_date: dateRange?.[0]?.startOf('day').toISOString(),
                        to_date: dateRange?.[1]?.endOf('day').toISOString(),
                    },
                    withCredentials: true,
                });
                const data: MeasurementItem[] = res.data?.data || [];
                if (offset === 0) {
                    total = Number(res.data?.total || 0);
                }
                all.push(...data);
                if (data.length < batchSize) break;
                offset += batchSize;
                if (total && all.length >= total) break;
            }

            setMeasurements(all);
            setPage(1);
        } catch (error) {
            console.error('Failed to load measurements', error);
            setMeasurements([]);
        } finally {
            setLoading(false);
        }
    }, [dateRange]);

    useEffect(() => {
        let mounted = true;
        const loadAreas = async () => {
            try {
                const res = await axios.get(ENDPOINTS.AREAS_V2, { withCredentials: true });
                const payload = res.data?.data || res.data || [];
                const map = new Map<number, { id: number; name: string; parentAreaId?: number | null }>();
                const stack = Array.isArray(payload) ? [...payload] : [];
                while (stack.length) {
                    const area = stack.pop();
                    if (area && typeof area.id === 'number') {
                        map.set(area.id, {
                            id: area.id,
                            name: area.name,
                            parentAreaId: area.parentAreaId ?? null,
                        });
                    }
                    if (area?.children?.length) {
                        stack.push(...area.children);
                    }
                }
                if (mounted) {
                    setAreaIndex(map);
                }
            } catch (error) {
                console.warn('Failed to load areas for hierarchy', error);
            }
        };
        loadAreas();
        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        fetchMeasurements();
    }, [fetchMeasurements]);

    useEffect(() => {
        if (!dateRange?.[0] || !dateRange?.[1]) return;
        localStorage.setItem('measurementListDateRange', JSON.stringify({
            from: dateRange[0].format('YYYY-MM-DD'),
            to: dateRange[1].format('YYYY-MM-DD'),
        }));
    }, [dateRange]);

    const loadFilesForMeasurement = useCallback(async (measurement: MeasurementItem) => {
        setFilesLoading(true);
        try {
            const res = await axios.get(ENDPOINTS.MEASUREMENT_FILES_BATCH_URLS(measurement.id), {
                withCredentials: true,
            });
            const files: MeasurementFile[] = res.data?.data?.files || [];
            setSelectedFiles(filterProcessed(files));
        } catch (error) {
            console.warn('Unable to load signed URLs, falling back to raw file list', error);
            setSelectedFiles(filterProcessed(measurement.files || []));
        } finally {
            setFilesLoading(false);
        }
    }, [filterProcessed]);

    const handleRowClick = useCallback((measurement: MeasurementItem) => {
        const hydrated = {
            ...measurement,
            values: filterProcessed(measurement.values),
            files: filterProcessed(measurement.files),
            texts: filterProcessed(measurement.texts),
        };
        setSelectedMeasurement(hydrated);
        setModalOpen(true);
        loadFilesForMeasurement(measurement);
    }, [filterProcessed, loadFilesForMeasurement]);

    const valueTypes = useMemo(() => {
        const set = new Set<string>();
        measurements.forEach((m) => {
            filterProcessed(m.values || []).forEach((v) => {
                if (v?.valueType) set.add(v.valueType);
            });
        });
        return Array.from(set);
    }, [measurements, filterProcessed]);

    const rows = useMemo(() => {
        return measurements.map((m) => {
            const areaId = m.area?.id || m.areaId || null;
            const hasArea = Boolean(areaId);
            const patternId = m.pattern?.id || m.patternId || null;
            const patternPointId = m.patternPoint?.id || m.patternPointId || null;
            const areaPath: Array<{ id: number; name: string }> = [];
            if (areaId && areaIndex.size > 0) {
                let cursor = areaIndex.get(areaId);
                const safety = new Set<number>();
                while (cursor && !safety.has(cursor.id)) {
                    areaPath.unshift({ id: cursor.id, name: cursor.name });
                    safety.add(cursor.id);
                    cursor = cursor.parentAreaId ? areaIndex.get(cursor.parentAreaId) : undefined;
                }
            } else if (hasArea && m.area?.name) {
                areaPath.push({ id: areaId as number, name: m.area.name });
            }
            const areaDisplay = hasArea
                ? (areaPath.length ? areaPath.map((a) => a.name).join(' / ') : '—')
                : 'Unassigned';
            const row: any = {
                key: m.id,
                measurement: m,
                measurementId: m.id,
                areaId,
                patternId,
                patternPointId,
                measurementCode: m.measurementCode,
                areaName: areaDisplay,
                areaPath,
                deviceLabel: m.device?.label || '—',
                localPoseLabel: formatLocalPose(m.localPose),
                patternName: hasArea
                    ? (m.pattern?.name || '—')
                    : '—',
                patternPointLabel: hasArea
                    ? (m.patternPoint?.label ||
                        (m.patternPoint?.pointIndex !== undefined
                            ? `Point ${m.patternPoint.pointIndex}`
                            : '—'))
                    : '—',
                measuredAt: m.measuredAt || null,
                createdAt: m.createdAt || null,
            };
            filterProcessed(m.values || []).forEach((v) => {
                row[`value_${v.valueType}`] = v.numValue ?? v.textValue ?? null;
            });
            return row;
        });
    }, [measurements, filterProcessed, areaIndex]);

    useEffect(() => {
        if (rows.length === 0) {
            setSelectedRowKeys([]);
            return;
        }
        setSelectedRowKeys((prev) =>
            prev.filter((key) => rows.some((row: any) => row.measurementId === key))
        );
    }, [rows]);

    const columns = useMemo(() => {
        const renderLink = (
            label: string,
            onClick: (e: React.MouseEvent) => void,
            title?: string,
            linkClassName?: string
        ) => (
            <a
                href="#"
                onClick={onClick}
                className={`measurement-list-report-link ${linkClassName || ''}`.trim()}
                style={{
                    color: '#1890ff',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    fontWeight: 500,
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.textDecoration = 'underline';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.textDecoration = 'none';
                }}
                title={title}
            >
                {label}
            </a>
        );

        const base = [
            {
                title: 'Area',
                dataIndex: 'areaName',
                key: 'areaName',
                sorter: buildSorter('areaName', 'text'),
                filters: buildFilters(rows, 'areaName'),
                onFilter: (value: any, record: any) => normalizeToString(record.areaName) === value,
                render: (value: any, record: any) => {
                    if (!record.areaId) return value;
                    if (!Array.isArray(record.areaPath) || record.areaPath.length === 0) {
                        return renderLink(
                            value,
                            (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                goToReports(
                                    { areaId: record.areaId },
                                    record.measuredAt || record.createdAt
                                );
                            },
                            `View report for ${value}`,
                            'measurement-list-area-link'
                        );
                    }
                    return (
                        <Space size={6} wrap>
                            {record.areaPath.map((segment: { id: number; name: string }, idx: number) => (
                                <span key={segment.id}>
                                    {renderLink(
                                        segment.name,
                                        (e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            goToReports(
                                                { areaId: segment.id },
                                                record.measuredAt || record.createdAt
                                            );
                                        },
                                        `View report for ${segment.name}`,
                                        'measurement-list-area-link'
                                    )}
                                    {idx < record.areaPath.length - 1 ? <Text type="secondary">/</Text> : null}
                                </span>
                            ))}
                        </Space>
                    );
                },
            },
            {
                title: 'Pattern',
                dataIndex: 'patternName',
                key: 'patternName',
                sorter: buildSorter('patternName', 'text'),
                filters: buildFilters(rows, 'patternName'),
                onFilter: (value: any, record: any) => normalizeToString(record.patternName) === value,
                render: (value: any, record: any) => {
                    if (!record.areaId || !record.patternId || value === '—') return value;
                    return renderLink(
                        value,
                        (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            goToReports(
                                { areaId: record.areaId, patternId: record.patternId },
                                record.measuredAt || record.createdAt
                            );
                        },
                        `View pattern reports for ${value}`,
                        'measurement-list-pattern-link'
                    );
                },
            },
            {
                title: 'Pattern Point',
                dataIndex: 'patternPointLabel',
                key: 'patternPointLabel',
                sorter: buildSorter('patternPointLabel', 'text'),
                filters: buildFilters(rows, 'patternPointLabel'),
                onFilter: (value: any, record: any) => normalizeToString(record.patternPointLabel) === value,
                render: (value: any, record: any) => {
                    if (!record.areaId || !record.patternId || !record.patternPointId || value === '—') return value;
                    return renderLink(
                        value,
                        (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            goToReports({
                                areaId: record.areaId,
                                patternId: record.patternId,
                                patternPointId: record.patternPointId,
                            }, record.measuredAt || record.createdAt);
                        },
                        `View pattern point reports for ${value}`,
                        'measurement-list-pattern-point-link'
                    );
                },
            },
            {
                title: 'Measurement Code',
                dataIndex: 'measurementCode',
                key: 'measurementCode',
                sorter: buildSorter('measurementCode', 'text'),
                filters: buildFilters(rows, 'measurementCode'),
                onFilter: (value: any, record: any) => normalizeToString(record.measurementCode) === value,
                render: (value: any, record: any) => renderLink(
                    value,
                    (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        navigate(`/measurements/${record.measurementId}`);
                    },
                    `View measurement ${value}`,
                    'measurement-list-code-link'
                ),
            },
            {
                title: 'Device',
                dataIndex: 'deviceLabel',
                key: 'deviceLabel',
                sorter: buildSorter('deviceLabel', 'text'),
                filters: buildFilters(rows, 'deviceLabel'),
                onFilter: (value: any, record: any) => normalizeToString(record.deviceLabel) === value,
            },
            {
                title: 'Local Position',
                dataIndex: 'localPoseLabel',
                key: 'localPoseLabel',
                sorter: buildSorter('localPoseLabel', 'text'),
                filters: buildFilters(rows, 'localPoseLabel'),
                onFilter: (value: any, record: any) => normalizeToString(record.localPoseLabel) === value,
            },
            {
                title: <span className="measurement-list-measured-at-col">Measured At</span>,
                dataIndex: 'measuredAt',
                key: 'measuredAt',
                sorter: buildSorter('measuredAt', 'date'),
                filters: buildFilters(
                    rows.map((row: any) => ({
                        ...row,
                        measuredAt: measurementDateKey(row.measuredAt, row.measurement),
                    })),
                    'measuredAt'
                ),
                onFilter: (value: any, record: any) =>
                    record.measuredAt
                        ? measurementDateKey(record.measuredAt, record.measurement) === value
                        : value === '',
                render: (value: any, record: any) => formatMeasurementTimestamp(value, record.measurement, { seconds: false }),
            },
            {
                title: <span className="measurement-list-created-at-col">Uploaded At</span>,
                dataIndex: 'createdAt',
                key: 'createdAt',
                sorter: buildSorter('createdAt', 'date'),
                filters: buildFilters(
                    rows.map((row: any) => ({
                        ...row,
                        createdAt: row.createdAt ? new Date(row.createdAt).toISOString().slice(0, 10) : '',
                    })),
                    'createdAt'
                ),
                onFilter: (value: any, record: any) =>
                    record.createdAt
                        ? new Date(record.createdAt).toISOString().slice(0, 10) === value
                        : value === '',
                render: (value: any) => formatUtcTimestamp(value),
            },
        ];

        const valueCols = valueTypes.map((valueType) => {
            const key = `value_${valueType}`;
            const nonNullValues = rows
                .map((row: any) => row[key])
                .filter((value: any) => value !== null && value !== undefined);
            const isNumeric = nonNullValues.length > 0 && nonNullValues.every((value: any) => typeof value === 'number');
            const sorterType: SorterType = isNumeric ? 'number' : 'text';
            return {
                title: <span className="measurement-list-metric-col" data-value-type={valueType}>{valueType}</span>,
                dataIndex: key,
                key,
                sorter: buildSorter(key, sorterType),
                filters: buildFilters(rows, key),
                onFilter: (value: any, record: any) => normalizeToString(record[key]) === value,
                render: (value: any) => formatValue(value),
            };
        });

        return [...base, ...valueCols];
    }, [goToReports, rows, valueTypes]);

    const selectedMeasurementIds = useMemo(() => {
        return selectedRowKeys
            .map((key) => Number(key))
            .filter((id) => !Number.isNaN(id));
    }, [selectedRowKeys]);

    const csvRows = useMemo(() => {
        return rows.map((row: any) => {
            const formatted: Record<string, any> = {
                measurementId: row.measurementId,
                area: row.areaName,
                pattern: row.patternName,
                patternPoint: row.patternPointLabel,
                measurementCode: row.measurementCode,
                device: row.deviceLabel,
                localPosition: row.localPoseLabel,
                measuredAtUtc: row.measuredAt ? new Date(row.measuredAt).toISOString() : '',
                measuredAtLocal: formatMeasurementTimestamp(row.measuredAt, row.measurement, { includeZone: false }),
                measuredTimezone: measurementTimezone(row.measurement),
                uploadedAtUtc: row.createdAt ? new Date(row.createdAt).toISOString() : '',
            };
            valueTypes.forEach((valueType) => {
                const key = `value_${valueType}`;
                formatted[valueType] = row[key] ?? '';
            });
            return formatted;
        });
    }, [rows, valueTypes]);

    const csvKeys = useMemo(() => ([
        'area',
        'pattern',
        'patternPoint',
        'measurementCode',
        'device',
        'localPosition',
        'measuredAtUtc',
        'measuredAtLocal',
        'measuredTimezone',
        'uploadedAtUtc',
        ...valueTypes,
    ]), [valueTypes]);

    const handleDownloadCsv = useCallback((mode: 'all' | 'selected') => {
        const selectedSet = new Set(selectedMeasurementIds);
        const targetRows =
            mode === 'selected'
                ? csvRows.filter((row: any) => selectedSet.has(row.measurementId))
                : csvRows;
        if (!targetRows.length) {
            message.warning('No measurements to export');
            return;
        }
        const header = csvKeys
            .map((key) => escapeCsvValue(key))
            .join(',');
        const lines = targetRows.map((row: Record<string, any>) =>
            csvKeys.map((key) => escapeCsvValue(row[key])).join(',')
        );
        const csvContent = [header, ...lines].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const filename =
            mode === 'selected'
                ? `measurements-selected-${dayjs().format('YYYY-MM-DD')}.csv`
                : `measurements-${dayjs().format('YYYY-MM-DD')}.csv`;
        downloadBlob(blob, filename);
    }, [csvKeys, csvRows, selectedMeasurementIds]);

    const handleDownloadZip = useCallback(async (mode: 'all' | 'selected') => {
        const targetIds = mode === 'selected' ? selectedMeasurementIds : rows.map((row: any) => row.measurementId);
        if (!targetIds.length) {
            message.warning('No measurements to export');
            return;
        }
        setDownloadingZip(true);
        try {
            const res = await axios.post(
                ENDPOINTS.MEASUREMENTS_BATCH_FILES_ZIP,
                { measurementIds: targetIds },
                { withCredentials: true, responseType: 'blob' }
            );
            const filename =
                mode === 'selected'
                    ? `measurements-selected-${dayjs().format('YYYY-MM-DD')}.zip`
                    : `measurements-export-${dayjs().format('YYYY-MM-DD')}.zip`;
            downloadBlob(res.data, filename);
        } catch (error) {
            console.error('Failed to download measurement files', error);
            message.error('Failed to download measurement files');
        } finally {
            setDownloadingZip(false);
        }
    }, [rows, selectedMeasurementIds]);

    const selectionMenuItems = useMemo(() => ([
        { key: 'select-page', label: 'Select this page' },
        { key: 'select-all', label: 'Select all results' },
        { key: 'clear', label: 'Clear selection' },
    ]), []);

    const handleSelectionMenu = useCallback((key: string) => {
        if (key === 'select-page') {
            const start = (page - 1) * pageSize;
            const end = start + pageSize;
            setSelectedRowKeys(rows.slice(start, end).map((row: any) => row.measurementId));
            return;
        }
        if (key === 'select-all') {
            setSelectedRowKeys(rows.map((row: any) => row.measurementId));
            return;
        }
        if (key === 'clear') {
            setSelectedRowKeys([]);
        }
    }, [page, pageSize, rows]);

    const selectionHeader = (
        <Dropdown
            menu={{
                items: selectionMenuItems,
                onClick: ({ key }) => handleSelectionMenu(String(key)),
            }}
            trigger={['click']}
        >
            <span className="measurement-list-selection-trigger" onClick={(e) => e.preventDefault()}>
                <Checkbox
                    checked={rows.length > 0 && selectedRowKeys.length === rows.length}
                    indeterminate={selectedRowKeys.length > 0 && selectedRowKeys.length < rows.length}
                    onChange={() => {
                        // no-op: checkbox is only a dropdown trigger
                    }}
                />
            </span>
        </Dropdown>
    );

    const imageFiles = useMemo(() => {
        return selectedFiles.filter((file) => (file.mimeType || '').startsWith('image/'));
    }, [selectedFiles]);

    const otherFiles = useMemo(() => {
        return selectedFiles.filter((file) => !(file.mimeType || '').startsWith('image/'));
    }, [selectedFiles]);
    const hasImages = imageFiles.length > 0;
    const hasOtherFiles = otherFiles.length > 0;

    return (
        <div className="measurement-list-page" style={{ padding: 24 }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Space align="center" wrap className="measurement-list-header">
                    <Title level={3} style={{ margin: 0 }}>Measurement list</Title>
                    <Tag color="blue">{rows.length} total</Tag>
                    <Button
                        type="primary"
                        icon={<ImportOutlined />}
                        onClick={() => navigate('/measurements/import')}
                    >
                        Import Measurements
                    </Button>
                    <RangePicker
                        className="measurement-list-date-range"
                        value={dateRange}
                        onChange={(range) => {
                            if (!range || !range[0] || !range[1]) return;
                            setDateRange(range as [Dayjs, Dayjs]);
                        }}
                        allowClear={false}
                    />
                    {latestUploadsHidden && (
                        <Button
                            type="primary"
                            onClick={() => setDateRange([dateRange[0], dayjs()])}
                        >
                            Show latest uploads
                        </Button>
                    )}
                </Space>

                <Card className="measurement-list-card">
                    <Space style={{ marginBottom: 12 }} wrap className="measurement-list-actions measurement-list-download-actions">
                        <Button
                            className="measurement-list-download-csv-button"
                            onClick={() => handleDownloadCsv('selected')}
                            disabled={selectedMeasurementIds.length === 0}
                        >
                            Download selected CSV
                        </Button>
                        <Button
                            className="measurement-list-download-zip-button"
                            onClick={() => handleDownloadZip('selected')}
                            loading={downloadingZip}
                            disabled={selectedMeasurementIds.length === 0}
                        >
                            Download selected files ZIP
                        </Button>
                        <Text type="secondary">
                            {selectedMeasurementIds.length > 0
                                ? `${selectedMeasurementIds.length} selected`
                                : 'Select measurements to enable downloads'}
                        </Text>
                    </Space>
                    <Table
                        dataSource={rows}
                        columns={columns}
                        className="measurement-list-table"
                        size="small"
                        loading={loading}
                        rowSelection={{
                            selectedRowKeys,
                            onChange: (keys) => setSelectedRowKeys(keys),
                            preserveSelectedRowKeys: true,
                            columnTitle: selectionHeader,
                            renderCell: (_checked, _record, _index, originNode) => (
                                <span className="measurement-list-row-checkbox">{originNode}</span>
                            ),
                        }}
                        pagination={{
                            current: page,
                            pageSize,
                            total: rows.length,
                            showSizeChanger: true,
                            pageSizeOptions: ['25', '50', '100', '200'],
                            onChange: (nextPage, nextSize) => {
                                setPage(nextPage);
                                if (nextSize && nextSize !== pageSize) {
                                    setPageSize(nextSize);
                                }
                            },
                        }}
                        scroll={{ x: 'max-content' }}
                        onRow={(record: any) => ({
                            onClick: (event) => {
                                const target = event.target as HTMLElement;
                                if (target.closest('.ant-checkbox-wrapper') || target.closest('a')) {
                                    return;
                                }
                                handleRowClick(record.measurement);
                            },
                            style: { cursor: 'pointer' },
                        })}
                        locale={{
                            emptyText: loading ? 'Loading...' : 'No measurements found',
                        }}
                    />
                </Card>
            </Space>

            <Modal
                title={selectedMeasurement ? `Measurement ${selectedMeasurement.measurementCode}` : 'Measurement'}
                open={modalOpen}
                onCancel={() => {
                    setModalOpen(false);
                    setSelectedMeasurement(null);
                    setSelectedFiles([]);
                }}
                footer={[
                    <Button
                        key="details"
                        type="primary"
                        disabled={!selectedMeasurement}
                        onClick={() => {
                            if (!selectedMeasurement) return;
                            navigate(`/measurements/${selectedMeasurement.id}`);
                        }}
                    >
                        Show details
                    </Button>,
                    <Button key="close" onClick={() => setModalOpen(false)}>Close</Button>,
                ]}
                width={900}
            >
                {!selectedMeasurement ? (
                    <Text type="secondary">No measurement selected.</Text>
                ) : (
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <Space direction="vertical" size={8} style={{ width: '100%' }}>
                            <Text strong>Values</Text>
                            {selectedMeasurement.values && selectedMeasurement.values.length > 0 ? (
                                <List
                                    size="small"
                                    style={{ marginTop: 4 }}
                                    dataSource={selectedMeasurement.values}
                                    renderItem={(v) => (
                                        <List.Item>
                                            <Space split={<Divider type="vertical" />}>
                                                <Text>{v.valueType}</Text>
                                                <Text strong>{v.textValue ?? v.numValue ?? '—'}</Text>
                                            </Space>
                                        </List.Item>
                                    )}
                                />
                            ) : (
                                <Text type="secondary">No values</Text>
                            )}
                        </Space>

                        <Divider style={{ margin: '4px 0' }} />

                        <Space direction="vertical" size={8} style={{ width: '100%' }}>
                            <Text strong>Notes</Text>
                            {selectedMeasurement.texts && selectedMeasurement.texts.length > 0 ? (
                                <List
                                    size="small"
                                    style={{ marginTop: 4 }}
                                    dataSource={selectedMeasurement.texts}
                                    renderItem={(t) => (
                                        <List.Item>
                                            <List.Item.Meta
                                                title={(
                                                    <Space split={<Divider type="vertical" />} size="small">
                                                        <Tag color="blue">{t.textType || 'comment'}</Tag>
                                                        <Text type="secondary">
                                                            {t.createdAt ? dayjs(t.createdAt).format('YYYY-MM-DD HH:mm') : '—'}
                                                        </Text>
                                                    </Space>
                                                )}
                                                description={<Text>{t.text || '—'}</Text>}
                                            />
                                        </List.Item>
                                    )}
                                />
                            ) : (
                                <Text type="secondary">No notes</Text>
                            )}
                        </Space>

                        <Divider style={{ margin: '4px 0' }} />

                        <Space direction="vertical" size={8} style={{ width: '100%' }}>
                            <Text strong>Files & Images</Text>
                            {filesLoading ? (
                                <Spin />
                            ) : hasImages || hasOtherFiles ? (
                                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                                    {hasImages && (
                                        <div>
                                            <Text type="secondary">Images</Text>
                                            <div style={{ marginTop: 8 }}>
                                                <Space size="middle" wrap>
                                                    {imageFiles.map((file) => (
                                                        <div key={file.id} style={{ width: 160 }}>
                                                            <Image
                                                                src={file.signedUrl || ''}
                                                                alt={file.fileName}
                                                                width="100%"
                                                                style={{ borderRadius: 6, border: '1px solid #f0f0f0' }}
                                                            />
                                                            <div style={{ marginTop: 4, fontSize: 12 }}>
                                                                <FileImageOutlined /> {file.fileName}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </Space>
                                            </div>
                                        </div>
                                    )}

                                    {hasOtherFiles && (
                                        <div>
                                            <Text type="secondary">Other files</Text>
                                            <List
                                                size="small"
                                                style={{ marginTop: 4 }}
                                                dataSource={otherFiles}
                                                renderItem={(file) => (
                                                    <List.Item>
                                                        <Text>{file.fileName}</Text>
                                                    </List.Item>
                                                )}
                                            />
                                        </div>
                                    )}
                                </Space>
                            ) : (
                                <Text type="secondary">No files or images</Text>
                            )}
                        </Space>
                    </Space>
                )}
            </Modal>
        </div>
    );
};

export default MeasurementList;
